import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../components/AppProvider';
import { OAuthCallbackLayout } from '../components/oauth/OAuthCallbackLayout';
import { dispatchTaskCompleted } from '../lib/onboarding/dispatchTaskCompleted';

export function GoogleAuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useApp();

  // Debug del componente
  console.log('GoogleAuthCallback component rendered', { 
    location: window.location.href,
    params: location.search,
    hasCode: location.search.includes('code=')
  });

  // Utilizziamo una ref per tracciare se l'operazione è già stata eseguita
  const authProcessedRef = React.useRef(false);
  
  // Attiva il logging di debug
  const debugLogEnabled = true;

  useEffect(() => {
    // Previeni esecuzioni multiple dello stesso effetto
    if (authProcessedRef.current) {
      return;
    }
    
    async function handleAuthCallback() {
      try {
        // Marca l'operazione come già eseguita
        authProcessedRef.current = true;
        
        // Parse URL params
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');
        const state = params.get('state');

        if (error) {
          setError(`Authentication error: ${error}`);
          setProcessing(false);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setProcessing(false);
          return;
        }

        // Exchange the code for tokens using a Supabase function
        console.log('Exchanging code for tokens, code length:', code?.length, 'code preview:', code?.substring(0, 10) + '...');
        
        // Verifica che la sessione utente sia valida prima di continuare
        const { data: { session } } = await supabase.auth.getSession();
        console.log('User session exists:', !!session, 'User logged in:', !!session?.user);
        
        if (!session || !session.user) {
          console.error('No active session found, cannot connect Google account');
          setError('Impossibile connettere l\'account Google: sessione non attiva. Effettua nuovamente il login.');
          setProcessing(false);
          return;
        }
        
        let tokenData;
        try {
          // Recupera il token di accesso per la chiamata alla funzione Supabase
          const accessToken = session.access_token;
          console.log('Access token available:', !!accessToken);
          
          console.log('Sending request to google-auth-token function with these parameters:',
            {
              code_length: code?.length,
              redirect_uri: 'https://app.flusia.it/digital-google-connect'
            }
          );
          
          // Dobbiamo usare l'edge function per ottenere il refresh token e i dati utente di Google
          // Non possiamo farlo direttamente dal browser per motivi di sicurezza
          console.log('Iniziando lo scambio del codice tramite la funzione edge google-auth-token');
          
          // Ottieni i token da Google tramite la nostra funzione Edge
          const { data, error: tokenError } = await supabase.functions.invoke('google-auth-token', {
            body: {
              code,
              // Deve essere esattamente lo stesso URL usato nella chiamata di autorizzazione
              redirectUri: 'https://app.flusia.it/digital-google-connect'
            }
          });
          
          if (tokenError || !data) {
            console.error('Errore durante lo scambio del codice:', tokenError || 'Nessun dato ricevuto');
            throw new Error('Non è stato possibile ottenere i token da Google. ' + 
              (tokenError ? tokenError.message : 'Nessun dato ricevuto'));
          }
          
          // Log molto dettagliato per debug
          if (debugLogEnabled) {
            console.log('===== DEBUG TOKEN RESPONSE =====');
            console.log('Token ricevuti con successo:', {
              hasRefreshToken: !!data.refresh_token,
              refreshTokenLength: data.refresh_token?.length,
              hasAccessToken: !!data.access_token,
              accessTokenLength: data.access_token?.length,
              hasUserInfo: !!data.user_info,
              userInfoEmail: data.user_info?.email,
              userInfoId: data.user_info?.id,
              userId: data.user_id,
              scopes: data.scopes,
              serverTime: data.server_time
            });
            
            // Log completo dell'oggetto (attenzione a non loggare in produzione)
            console.log('Token data completo (SOLO PER DEBUG):', JSON.stringify(data, null, 2));
          }
          
          // Verifica che abbiamo ricevuto i dati necessari
          if (!data.user_info) {
            throw new Error('Dati incompleti ricevuti da Google. ' + 
              'Mancano le informazioni utente.');
          }
          
          // È possibile che Google non fornisca un nuovo refresh token se l'utente ha già autorizzato l'app
          // In questo caso, dobbiamo gestire correttamente la situazione
          if (!data.refresh_token) {
            console.warn('Nessun refresh token ricevuto da Google. Questo può succedere se l\'utente ha già autorizzato l\'app.');
            
            // Verifichiamo se l'utente ha già un refresh token salvato
            const { data: existingProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('google_refresh_token, google_account_connected')
              .eq('id', session.user.id)
              .single();
              
            if (profileError) {
              console.error('Errore nel verificare il profilo esistente:', profileError);
              throw new Error('Impossibile verificare se esiste già un refresh token salvato.');
            }
            
            if (!existingProfile.google_refresh_token) {
              console.error('L\'utente non ha un refresh token salvato e Google non ne ha fornito uno nuovo.');
              throw new Error('Google non ha fornito un refresh token e non ne è stato trovato uno esistente. ' +
                'Prova a disconnettere e riconnettere l\'account Google, oppure usa un altro account Google.');
            }
            
            console.log('Utilizzo del refresh token esistente poiché Google non ne ha fornito uno nuovo.');
            // Assegniamo il token salvato ai dati per l'aggiornamento
            data.refresh_token = existingProfile.google_refresh_token;
          }
          
          // Prepara i dati per l'aggiornamento del profilo utente
          const updateData = {
            google_account_connected: true,
            google_refresh_token: data.refresh_token,
            google_account_email: data.user_info.email,
            google_user_id: data.user_info.id,
            google_workspace_scopes: data.scopes || [],
            updated_at: new Date().toISOString()
          };
          
          if (debugLogEnabled) {
            console.log('===== DEBUG UPDATE PROFILE =====');
            console.log('Dati preparati per aggiornamento profilo:', {
              userId: session.user.id,
              connected: updateData.google_account_connected,
              email: updateData.google_account_email,
              googleUserId: updateData.google_user_id, 
              hasRefreshToken: !!updateData.google_refresh_token,
              refreshTokenLength: updateData.google_refresh_token?.length,
              hasScopes: !!updateData.google_workspace_scopes && updateData.google_workspace_scopes.length > 0
            });
          }
          
          // Verifica che l'utente esista prima dell'aggiornamento
          const { data: existingUser, error: getUserError } = await supabase
            .from('user_profiles')
            .select('id, email')
            .eq('id', session.user.id)
            .single();
            
          if (getUserError) {
            console.error('Errore nel verificare l\'esistenza del profilo utente:', getUserError);
            throw new Error(`Impossibile verificare il profilo utente: ${getUserError.message}`);
          }
          
          console.log('Profilo utente esistente:', existingUser);
          
          // Salva i dati dell'account Google nel profilo utente
          const { data: updatedProfile, error: updateError } = await supabase
            .from('user_profiles')
            .update(updateData)
            .eq('id', session.user.id)
            .select();
          
          if (updateError) {
            console.error('Errore durante l\'aggiornamento del profilo:', updateError);
            throw new Error(`Errore nell'aggiornamento del profilo: ${updateError.message}`);
          }
          
          console.log('Profilo aggiornato con successo:', {
            success: !!updatedProfile,
            rowsUpdated: updatedProfile?.length
          });
          
          console.log('Account Google collegato con successo al profilo:', {
            userId: session.user.id,
            googleEmail: data.user_info.email,
            googleId: data.user_info.id
          });
          
          // Assegna i dati per l'uso successivo
          tokenData = data;
          
          // Notifica all'utente
          toast.success('Account Google collegato con successo!');
          dispatchTaskCompleted('integrations', 'google_oauth_success');
          
          // Parse the state to determine where to navigate
          let redirectTo = '/';
          let showProfileModal = false;
          
          try {
            console.log('Trying to parse state:', state);
            if (state) {
              // Decodifica il parametro state
              const decodedState = decodeURIComponent(state);
              console.log('Decoded state:', decodedState);
              
              const stateObj = JSON.parse(decodedState);
              console.log('Parsed state object:', stateObj);
              
              if (stateObj.redirectTo) {
                // Aggiungi il parametro per il modale a qualsiasi URL di destinazione
                // In questo modo rispettiamo i reindirizzamenti dell'app e manteniamo la funzionalità
                redirectTo = stateObj.redirectTo;
                
                // Aggiungi il parametro openModal=google all'URL di destinazione
                // Preserva eventuali parametri esistenti
                if (redirectTo.includes('?')) {
                  // Se l'URL già contiene parametri di query, aggiungi il nostro parametro
                  redirectTo += '&openModal=google';
                } else {
                  // Altrimenti, inizia la stringa di query
                  redirectTo += '?openModal=google';
                }
                
                console.log('Redirecting to URL with modal parameter:', redirectTo);
                showProfileModal = true;
                console.log('Setting redirect to:', redirectTo);
              }
            }
          } catch (e) {
            console.error('Error parsing state:', e);
          }
          
          // Utilizziamo la history API per sostituire l'URL corrente
          // Questo rimuove i parametri di query di Google dall'URL
          window.history.replaceState(
            { showProfileModal }, // Conserva l'informazione che il modale dovrebbe essere aperto
            '', 
            redirectTo
          );
          
          // Aggiorna lo stato dell'URL e naviga alla destinazione finale
          setTimeout(() => {
            // Usa replace:true per sovrascrivere l'entry corrente nella history del browser
            navigate(redirectTo, { 
              replace: true,
              state: { showProfileModal } // Passa lo stato che il modale del profilo deve essere aperto
            });
          }, 1000);
          
          return;
        } catch (err) {
          console.error('Exception during token exchange:', err);
          setError(`Exception during token exchange: ${err.message}`);
          setProcessing(false);
          return;
        }

        // Questo codice non viene più eseguito perché c'è un return sopra
        // Lo lasciamo come riferimento per il momento
        console.warn('Code after the return statement was reached, which should not happen');
        setError('An unexpected error occurred. Please try again.');
        setProcessing(false);
      } catch (err) {
        console.error('Error in Google authentication callback:', err);
        setError('An unexpected error occurred. Please try again.');
        setProcessing(false);
        // Prevenire navigazione automatica in caso di errore
        return;
      }
    }

    handleAuthCallback();
  }, [location, navigate, toast]);

  const googleLogo = (
    <svg className="h-10 w-10" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"
      />
      <path
        fill="#FF3D00"
        d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"
      />
    </svg>
  );

  if (processing && !error) {
    return (
      <OAuthCallbackLayout
        status="processing"
        title="Connessione a Google in corso"
        description="Stiamo completando il collegamento del tuo account Google."
        providerLogo={googleLogo}
      />
    );
  }

  if (error) {
    return (
      <OAuthCallbackLayout
        status="error"
        title="Connessione non riuscita"
        description={error}
        providerLogo={googleLogo}
        primaryAction={{
          label: 'Torna alla dashboard',
          onClick: () => navigate('/'),
        }}
        secondaryAction={{
          label: 'Riprova',
          onClick: () => navigate('/settings'),
        }}
      />
    );
  }

  return (
    <OAuthCallbackLayout
      status="success"
      title="Account Google collegato"
      description="Il tuo account Google è stato collegato correttamente. Ti stiamo reindirizzando..."
      providerLogo={googleLogo}
    />
  );
}